import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { LocalizationProvider, MobileDatePicker } from "@mui/x-date-pickers";
import MomentUtils from "@date-io/moment";
import moment from "moment";
import "moment/locale/ar";
import { TextField } from "@mui/material";
import DatePickerActionBar from "./CustomComponents/DatePicker/DatePickerActionBar";
import { useSelector } from "react-redux";
import { getTranslation } from "orion-components/i18n";
import { styled } from "@mui/material/styles";

const darkThemeSx = {
	"& .MuiDayPicker-weekContainer": {
		"& .Mui-disabled": {
			color: "#7C7C7D !important"
		}
	},
	"& .MuiPickersDay-dayWithMargin": {
		color: "#fff",
		backgroundColor: "#2C2C2E"
	},

	"& .MuiCalendarPicker-root": {
		color: "#fff",
		backgroundColor: "#2C2C2E"
	},
	"& .MuiTypography-h4": {
		fontSize: "1.5rem"
	},
	"& .MuiPickersToolbar-penIconButton": {
		display: "none !important"
	}
};
const lightThemeSx = {
	"& .MuiTypography-h4": {
		fontSize: "1.5rem"
	},
	"& .MuiPickersToolbar-penIconButton": {
		display: "none !important"
	}
};

const CustomizedTextField = styled(TextField)(({ customStyles }) => ({
	"& label": {
		color: "#9A9FA3",
		fontSize: 12,
		...customStyles.label
	},
	"& label.Mui-focused": {
		color: "#9A9FA3",
		...customStyles.labelFocused
	},
	"& .MuiInputBase-input.MuiOutlinedInput-input.MuiInputBase-inputSizeSmall": {
		padding: "0px !important",
		...customStyles.outlinedInput
	},
	"& .MuiList-root MuiList-padding MuiMenu-list": {
		background: "black"
	},
	"& .MuiOutlinedInput-root": {
		"& fieldset": {
			borderColor: "#5E6571",
			width: 1,
			...customStyles.fieldset
		},
		"&:hover fieldset": {
			borderColor: "#4DB5F4",
			...customStyles.fieldsetHover
		},
		"&.Mui-focused fieldset": {
			borderColor: "#4DB5F4",
			...customStyles.fieldsetFocused
		},
		// date picker input field
		"&.MuiInputBase-root.MuiOutlinedInput-root.MuiInputBase-colorPrimary.MuiInputBase-formControl.MuiInputBase-sizeSmall.Mui-readOnly":
			{
				"& .MuiInputBase-input": {
					fontSize: 10,
					color: "#FFFFFF"
				}
			},
		...customStyles.inputRoot
	}
}));

const propTypes = {
	id: PropTypes.string,
	label: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
	fullWidth: PropTypes.bool,
	value: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
	handleChange: PropTypes.func.isRequired,
	format: PropTypes.string,
	minDate: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
	maxDate: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
	clearable: PropTypes.bool,
	disabled: PropTypes.bool,
	margin: PropTypes.string,
	options: PropTypes.object,
	customDialogProps: PropTypes.object,
	okLabel: PropTypes.bool,
	cancelLabel: PropTypes.bool,
	clearLabel: PropTypes.bool,
	autoOk: PropTypes.bool,
	dir: PropTypes.string,
	locale: PropTypes.string,
	theme: PropTypes.string,
	onSubmit: PropTypes.func,
	useInputControl: PropTypes.bool,
	showDialog: PropTypes.bool,
	externalDate: PropTypes.string,
	onExternalReset: PropTypes.func,
	slotProps: PropTypes.object
};

const defaultProps = {
	id: "date-picker",
	label: "",
	format: "MM/DD/YYYY",
	value: null,
	minDate: "1900-01-01",
	maxDate: "2075-01-01",
	clearable: true,
	disabled: false,
	margin: "normal",
	dir: "ltr",
	locale: "en",
	theme: "dark",
	clearLabel: true,
	okLabel: true,
	cancelLabel: true,
	options: {},
	customDialogProps: {},
	useCustomTextField: false,
	handleChange: () => {},
	onSubmit: () => {},
	useInputControl: true,
	showDialog: false,
	externalDate: null,
	onExternalReset: () => {},
	labelStyle: {}
};

const CBDatePicker = ({
	id,
	label,
	fullWidth,
	value,
	handleChange,
	minDate,
	maxDate,
	clearable,
	disabled,
	margin,
	options,
	customDialogProps,
	dir,
	okLabel,
	cancelLabel,
	clearLabel,
	autoOk,
	theme,
	labelStyle,
	useCustomTextField,
	onSubmit,
	useInputControl,
	showDialog,
	externalDate,
	onExternalReset
}) => {
	const locale = useSelector(state => state.i18n && state.i18n.locale);
	const localeOverride = locale === "ar_kw" ? "en" : locale;

	const [isPickerOpen, setPickerOpen] = useState(showDialog); // State to control picker visibility

	useEffect(() => {
		if (!useInputControl) {
			setPickerOpen(showDialog); // Automatically open when showDialog is true
		}
	}, [showDialog, useInputControl]);

	useEffect(() => {
		if (externalDate !== null) {
			setDateTime(externalDate);
		}
	}, [externalDate]);

	const styles = {
		inputLabelProps: {
			...(dir === "ltr" && { transformOrigin: "top left" }),
			...(dir === "rtl" && {
				transformOrigin: "top right",
				left: "unset",
				textAlign: "right"
			}),
			...labelStyle
		}
	};

	moment.locale(localeOverride); // supports date picker localization like calendar date translation and toolbar

	let tempDate = null;
	const [dateTime, setDateTime] = useState(value);
	const [ok, setOk] = useState(false);

	const resetDateTime = () => {
		if (tempDate === null && dateTime === null) {
			setDateTime(null);
		} else {
			setDateTime(dateTime);
		}
		onExternalReset();
	};

	const setActualDate = () => {
		setOk(!ok);
		tempDate = dateTime;
		handleChange(dateTime);
		onSubmit(dateTime);
	};

	const clearDate = () => {
		onSubmit(null);
	};

	const actions = [
		clearable && clearLabel ? getTranslation("global.CBComponents.DateTime.clear") : null,
		cancelLabel ? getTranslation("global.CBComponents.DateTime.cancel") : null,
		okLabel ? getTranslation("global.CBComponents.DateTime.ok") : null
	];

	let error;
	if (options?.error) {
		error = options.error;
	} else if (value === null) {
		error = false;
	} else {
		error = null;
	}

	return (
		<LocalizationProvider dateAdapter={MomentUtils} localeText={localeOverride} adapterLocale={localeOverride}>
			<MobileDatePicker
				{...(!useInputControl ? { open: isPickerOpen } : {})}
				{...options}
				id={id}
				label={label}
				onChange={val => {
					setDateTime(val);
					handleChange(val);
				}}
				value={dateTime}
				onOpen={() => {
					if (value === null && dateTime === null) {
						setDateTime(new Date());
					} else {
						setDateTime(moment(dateTime));
					}
				}}
				minDate={moment(minDate)}
				maxDate={moment(maxDate)}
				disabled={disabled}
				DialogProps={{
					style: { direction: "ltr", ...customDialogProps },
					className: "cbDateTimePicker",
					sx: {
						...(theme === "dark" ? darkThemeSx : lightThemeSx),
						zIndex: 2601
					}
				}}
				dayOfWeekFormatter={val => {
					return (
						<div
							style={{
								color: theme === "dark" ? "#fff" : "black"
							}}
						>
							{val}
						</div>
					);
				}}
				toolbarTitle={<div>&nbsp;</div>}
				closeOnSelect={autoOk}
				showToolbar={true}
				componentsProps={{
					actionBar: {
						actions,
						resetDateTime: resetDateTime,
						submit: setActualDate,
						clearDate: clearDate
					}
				}}
				components={{
					ActionBar: DatePickerActionBar
				}}
				renderInput={params => {
					if (!useInputControl) {
						return <></>;
					}
					return useCustomTextField ? (
						<CustomizedTextField
							variant="outlined"
							{...params}
							{...options}
							size="small"
							margin={margin}
							value={moment(value).format("MM/D/YYYY")}
							error={error}
							////inputProps={InputProps}
							InputLabelProps={{
								style: styles.inputLabelProps
							}}
						/>
					) : (
						<TextField
							{...params}
							{...options}
							variant="standard"
							value={moment(value).format("MM/D/YYYY")}
							fullWidth={fullWidth === undefined ? true : fullWidth}
							error={error}
							helperText={error}
							margin={margin}
							////inputProps={InputProps}
							InputLabelProps={{
								style: styles.inputLabelProps
							}} data-testid="orion-components-cbcomponents-date-time-cbdate-picker-text-field"
						/>
					);
				}} data-testid="orion-components-cbcomponents-date-time-cbdate-picker-mobile-date-picker"
			/>
		</LocalizationProvider>
	);
};

CBDatePicker.propTypes = propTypes;
CBDatePicker.defaultProps = defaultProps;

export default CBDatePicker;
